import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import {
  DELETE_SUB_COMPANY,
  GET_SUBCOMPANIES_BY_SERVICEID,
} from "../../graphql/subCompanyQueries";
import { GET_SERVICE_BY_ID } from "../../graphql/serviceQueries";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import CompanyView from "./CompanyView";
import SubCompanyForm from "../../components/directory/SubCompanyForm";
import Modal from "../../components/modal/Modal";
import { BsFiletypeCsv } from "react-icons/bs";
import { useToast } from "../../components/Toast/toastHook";
import useConfirmDialog from "../../components/ConfirmDialog/useConfirmDialog";
import TableToolbar from "../../components/TableToolbar/TableToolbar";
import useDebounce from "../../hooks/useDebounce";
import { toServerSort } from "../../utils/tableSort";
import { Button, Table } from "antd";
import {
  PlusOutlined as PlusIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
} from "@ant-design/icons";
import { TABLE_SCROLL } from "../../constants/table";
import useIsPhone from "../../hooks/useIsPhone";
import CardList from "../../components/CardList/CardList";
import ListCard, { CardAction, initialsOf } from "../../components/CardList/ListCard";

// Columns the server can order the sub-companies by.
const SORTABLE_COLUMNS = ["name", "address", "phone", "website"];

const Service = () => {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
  });

  const { serviceId } = useParams();
  const directoryName = useSearchParams()[0].get("name");
  const [subCompanyListings, setSubCompanyListings] = useState([]);
  const [exportOption, setExportOption] = useState([]);

  const toast = useToast(4000);
  const isPhone = useIsPhone();
  const { confirm, dialog } = useConfirmDialog();
  const [deleteSubCompany] = useMutation(DELETE_SUB_COMPANY);

  // Company View
  const [companyView, setCompanyView] = useState(false);
  // const [dropDown, setDropDown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formType, setFormType] = useState("add");

  // For edit
  const [subCompany, setSubCompany] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [details, setDetails] = useState(null);
  const [serviceName, setServiceName] = useState("");

  // For view Contacts
  const [subCompanyId, setSubCompanyId] = useState("");

  const [searchText, setSearchText] = useState("");
  const debounceValue = useDebounce(searchText, 500);
  const [sort, setSort] = useState(null);

  const { loading, error, data, refetch } = useQuery(GET_SUBCOMPANIES_BY_SERVICEID, {
    refetchQueries: [{ query: GET_SUBCOMPANIES_BY_SERVICEID }],
    variables: {
      serviceId,
      pagination: {
        page: pagination.page,
        limit: pagination.limit,
      },
      ...(debounceValue && { searchText: debounceValue }),
      ...(sort && { sort }),
    },
    fetchPolicy: "no-cache",
  });

  const {  data: exportData, refetch: exportRefetch } = useQuery(GET_SUBCOMPANIES_BY_SERVICEID, {
    refetchQueries: [{ query: GET_SUBCOMPANIES_BY_SERVICEID }],
    variables: {
      serviceId,
    },
    fetchPolicy: "no-cache",
  });

  const { data: serviceData, loading: serviceLoading } = useQuery(
    GET_SERVICE_BY_ID,
    {
      variables: {
        serviceId,
      },
    }
  );

  const onChange = (nextPagination, _tableFilters, sorter, extra) => {
    // A new sort order starts again from the first page.
    if (extra?.action === "sort") {
      setSort(toServerSort(sorter, SORTABLE_COLUMNS));
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    setPagination((prev) => ({
      ...prev,
      page: nextPagination?.current,
      limit: nextPagination?.pageSize,
    }));
  };

  // A new search starts again from the first page.
  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    exportRefetch()
    if (data) {
      setSubCompanyListings(data?.getSubCompanyByServiceID?.subCompanies);
      setPagination((prev) => ({
        ...prev,
        totalCount: data?.getSubCompanyByServiceID?.totalCount,
      }));
    }
    if (serviceData) {
      setServiceName(serviceData?.getServiceById?.title);
    }
    if(exportData){
      setExportOption(exportData?.getSubCompanyByServiceID?.subCompanies);
    }
  }, [data, loading, serviceData, exportData]);

  const handleModalRender = () => {
    setFormType("add");
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleEdit = (subCompany) => {
    setSubCompany(subCompany);
    setFormType("edit");
    setShowModal(true);
  };

  const handleDelete = (record) =>
    confirm({
      title: `Delete ${record.name}?`,
      description:
        "This company will be permanently deleted. This action cannot be undone.",
      onConfirm: async () => {
        try {
          const { data: res } = await deleteSubCompany({
            variables: { subCompanyId: record._id },
          });
          if (res?.deleteSubCompany) {
            toast("success", "Company deleted successfully");
          } else {
            toast("error", "Company not deleted or does not exist");
          }
        } catch (error) {
          toast("error", "Failed to delete company");
        }
        refetch();
      },
    });

  const handleShowCompanyView = () => {
    setCompanyView(true);
  };

  // The company's contacts page — opened from its name or the + button.
  const openCompany = (record) => {
    setSubCompany(record._id);
    setSubCompanyId(record._id);
    setCompanyName(serviceName);
    setDetails(record.details);
    handleShowCompanyView();
  };

  const columns = [
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (text, record) => (
        <span
          className="cursor-pointer hover:underline text-black"
          onClick={() => openCompany(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      sorter: true,
      render: (text) => (
        <span className="font-medium text-black">{text || "No Address"}</span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone",
      sorter: true,
      render: (text) => (
        <span className="font-medium text-black">
          {text || "No Phone Number"}
        </span>
      ),
    },
    {
      title: "Website",
      dataIndex: "website",
      key: "website",
      sorter: true,
      render: (text, record) => (
        <span className="font-medium text-black truncate">
          {text || record.url || "No Website"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <Button
            className="border-secondary text-secondary"
            icon={<PlusIcon />}
            onClick={() => openCompany(record)}
          />
          <Button
            className="border-primary text-primary"
            icon={<EditIcon />}
            onClick={() => handleEdit(record)}
          />
          <Button
            danger
            icon={<DeleteIcon />}
            onClick={() => handleDelete(record)}
          />
        </div>
      ),
    },
  ];

  const COLUMN_MAPPINGS = {
    name: "Company Name",
    address: "Address",
    phone: "Company Phone",
    website: "Company Website",
    serviceAreas: "Service Areas",
    notes: "Notes",
  };

  const handleExportAllColumns = () => {
    exportCompany(Object.keys(COLUMN_MAPPINGS));
  };

  const exportCompany = (checkedColumns) => {
    if (exportOption.length === 0) {
      toast("error", "No company exists.");
      return;
    }

    try {
      const companyCSVData = [];
      exportOption.forEach((subCompany) => {
        const columnsData = {};

        for (const column of checkedColumns) {
          switch (column) {
            case "name":
              columnsData[COLUMN_MAPPINGS[column]] =
                subCompany[column] || "No data";
              break;
            case "address":
              columnsData[COLUMN_MAPPINGS[column]] =
                subCompany[column] || "No data";
              break;
            case "phone":
              columnsData[COLUMN_MAPPINGS[column]] =
                subCompany[column] || "No data";
              break;
            case "website":
              columnsData[COLUMN_MAPPINGS[column]] =
                subCompany[column] || "No data";
              break;
            case "serviceAreas":
              columnsData[COLUMN_MAPPINGS[column]] =
                subCompany[column] || "No data";
              break;
            case "notes":
              columnsData[COLUMN_MAPPINGS[column]] =
                subCompany[column] || "No data";
              break;
            default:
              break;
          }
        }
        companyCSVData.push(columnsData);
      });

      const csvData = companyCSVData.map((user) =>
        Object.values(user)
          .map((value) => JSON.stringify(value))
          .join(",")
      );

      const csvHeaders = Object.keys(companyCSVData[0]);
      csvData.unshift(csvHeaders.join(","));

      const csvContent = csvData.join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "company_data.csv";

      a.click();

      window.URL.revokeObjectURL(url);

      toast("success", "Company data exported successfully.");
    } catch (error) {
      console.log("error in exportCompany", error.message);
      toast("error", "There was an error in exporting company details.");
    }
  };

  const firstLoad = loading && !subCompanyListings?.length;

  const emptyState = error ? (
    <ErrorState onRetry={() => refetch()} />
  ) : searchText ? (
    <EmptyState
      title="No companies match your search"
      description={`Nothing found for "${searchText}".`}
      actionLabel="Clear search"
      onAction={() => handleSearch("")}
    />
  ) : (
    <EmptyState
      title="No companies yet"
      description="Companies added to this service will appear here."
    />
  );

  // Phones: the same columns and the same three buttons as a table row.
  const companyCard = (record) => (
    <ListCard
      name={record.name}
      title={
        <button
          type="button"
          className="text-left hover:underline"
          onClick={() => openCompany(record)}
        >
          {record.name}
        </button>
      }
      initials={initialsOf(record.name)}
      subtitle={record.address || "No Address"}
      actions={
        <>
          <CardAction
            label={`Contacts for ${record.name}`}
            className="border-secondary text-secondary"
            icon={<PlusIcon />}
            onClick={() => openCompany(record)}
          />
          <CardAction
            label={`Edit ${record.name}`}
            className="border-primary text-primary"
            icon={<EditIcon />}
            onClick={() => handleEdit(record)}
          />
          <CardAction
            label={`Delete ${record.name}`}
            danger
            icon={<DeleteIcon />}
            onClick={() => handleDelete(record)}
          />
        </>
      }
    >
      <span>{record.phone || "No Phone Number"}</span>
      <span className="break-all">{record.website || record.url || "No Website"}</span>
    </ListCard>
  );

  return (
    <>
      {/* <CompaniesDropdown
        open={dropDown}
        setData={setSubCompanyListings}
        setOpen={setDropDown}
        directoryId={serviceId}
        data={companyData}
        loading={companyLoading}
      /> */}
      {!companyView && (
        <>
          <Breadcrumb
            pageName="Service List -"
            span={serviceLoading ? "" : serviceName}
          />
          <div className="pb-6">
            <div className="mb-4 flex justify-between items-center flex-wrap gap-6">
              <div className="flex justify-start items-center flex-wrap gap-6">
                <div className="font-medium text-black">Export Companies :</div>
                <div>
                  <BsFiletypeCsv
                    size={40}
                    className="hover:cursor-pointer hover:text-blue"
                    onClick={handleExportAllColumns}
                  />
                </div>
              </div>
            </div>
            <Card>
              <TableToolbar
                title="Companies"
                count={pagination?.totalCount || 0}
                searchValue={searchText}
                onSearch={handleSearch}
                action={
                  <button
                    onClick={handleModalRender}
                    className="bg-[#B4C540] h-[36px] px-5 text-center rounded-xl text-[#FFFFFF]"
                  >
                    + Add Company
                  </button>
                }
              />
              {isPhone ? (
                <CardList
                  className="px-4 pb-4"
                  label="Companies"
                  loading={loading}
                  items={subCompanyListings ?? []}
                  rowKey={(record) => record._id}
                  empty={emptyState}
                  renderItem={companyCard}
                  pagination={{
                    current: pagination.page,
                    pageSize: pagination.limit,
                    total: pagination.totalCount,
                    onChange: (page) => setPagination((prev) => ({ ...prev, page })),
                  }}
                  columns={columns}
                  sort={
                    sort
                      ? { columnKey: sort.field, order: sort.order === "asc" ? "ascend" : "descend" }
                      : {}
                  }
                  onSortChange={(sorter) => {
                    setSort(toServerSort(sorter, SORTABLE_COLUMNS));
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                />
              ) : (
                <Table
                  scroll={TABLE_SCROLL}
                  bordered
                  rowKey={(record) => record._id}
                  onChange={onChange}
                  {...(firstLoad
                    ? skeletonTableProps(columns)
                    : {
                        columns,
                        dataSource: subCompanyListings,
                        pagination: {
                          position: ["bottomCenter"],
                          current: pagination.page,
                          pageSize: pagination.limit,
                          total: pagination.totalCount,
                        },
                      })}
                  // The Add Company button lives in the table title, so the
                  // table stays mounted on failure and the empty slot carries
                  // the error instead of claiming there is nothing here.
                  locale={{ emptyText: emptyState }}
                />
              )}
              {error && !!subCompanyListings?.length && (
                <ErrorBanner onRetry={() => refetch()} />
              )}
            </Card>
          </div>
          <Modal
            title={formType === "add" ? "Add Company" : "Edit Company"}
            showModal={showModal}
            setShowModal={(value) => {
              setShowModal(value);
              // if (!value) setOffice({});
            }}
          >
            <SubCompanyForm
              handleClose={handleClose}
              onDirectoryListAdded={() => {
                refetch();
              }}
              formType={formType}
              subCompany={subCompany}
              serviceId={serviceId}
            />
          </Modal>
          {dialog}
        </>
      )}
      {companyView && (
        <CompanyView
          subCompanyId={subCompanyId}
          companyId={subCompany}
          handleBackClick={() => setCompanyView(false)}
          directoryName={directoryName}
          companyName={companyName}
          details={details}
        />
      )}
    </>
  );
};

export default Service;
